import type { MilanStoresList } from '../../context';
import type { Store } from '../../clinics/clinics';

interface Location {
    id: string;
    metroName: string;
    name: string;
    value: string;
    state: string;
    urlPath?: string;
    salesforceValue?: string;
    address?: string;
    addressCity?: string;
    marketingCity?: string;
    geo?: Store['geo'];
    stateAbbrev?: string;
    zipCode?: string;
    phone?: string;
}

/**
 * Transforms milanStores array into locations array format expected by ConsultFormV2
 * @param milanStores - Array of Milan stores from site context
 * @returns Array of locations formatted for ConsultFormV2
 */
export const transformStoresForConsultForm = (milanStores: MilanStoresList): Location[] => {
    const locations: Location[] = [];

    milanStores?.forEach((state) => {
        state?.metros?.forEach((metro) => {
            const metroName = metro?.name || '';
            metro?.stores?.forEach((store) => {
                // Only include open stores
                if (store.open) {
                    // Calculate displayName using same logic as LocationsDropdown.js
                    // Format: "Metro Name" + ("Marketing City suffix" or "(Marketing City)")
                    const marketingCity = store.marketingCity || '';
                    let displayName = metroName;
                    if (marketingCity.includes(metroName)) {
                        // e.g., "Omaha West" -> "Omaha" + " West"
                        const suffix = marketingCity.replace(metroName, '').trim();
                        displayName = suffix ? `${metroName} ${suffix}` : metroName;
                    } else {
                        // e.g., "Salem" metro with "West Salem" city -> "Salem (West Salem)"
                        displayName = marketingCity ? `${metroName} (${marketingCity})` : metroName;
                    }

                    locations.push({
                        id: state.name,
                        metroName: metroName,
                        name: store.salesforceValue,
                        value: store.salesforceValue,
                        urlPath: store.urlPath,
                        salesforceValue: store.salesforceValue,
                        state: state.name,
                        address: store.address,
                        addressCity: store.addressCity,
                        marketingCity: displayName, // This prop is used for displaying clinic city in the form dropdown
                        geo: store.geo,
                        stateAbbrev: store.stateAbbrev,
                        zipCode: store.zipCode,
                        phone: store.phone,
                    });
                }
            });
        });
    });

    // Sort alphabetically by state, then by marketing city (displayed in dropdown)
    return locations.sort((a, b) => {
        return a.state.localeCompare(b.state) || (a.marketingCity || '').localeCompare(b.marketingCity || '');
    });
};
